import React, { useState } from 'react';
import axiosClient from '../../lib/auth';
import { decodeToken } from '../../lib/tokenReset';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { CheckCircle2, Loader2 } from 'lucide-react';

type ResetPasswordPageParams = {
  rawToken: string;
};

export default function ResetPasswordGo() {
  const { rawToken } = useParams<ResetPasswordPageParams>();

  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [resetError, setResetError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setResetError('');
    setPasswordError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const decodedToken = decodeToken(rawToken);
    if (decodedToken === null) {
      setResetError('Invalid link token, please ensure to copy the entire URL from the email.');
      return;
    }

    const trimmedPassword = password.trim();

    if (!trimmedPassword) {
      setPasswordError('Password is required');
      return;
    }

    if (trimmedPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters');
      return;
    }

    if (trimmedPassword.length > 128) {
      setPasswordError('Password must be no more than 128 characters');
      return;
    }

    setIsLoading(true);
    setResetError('');

    try {
      const response = await axiosClient.post(`/Auth/DoTokenReset`, {
        password: trimmedPassword,
        token: decodedToken.token,
        userId: decodedToken.userId,
      });

      if (response.data.message) {
        setIsSuccess(true);
      } else {
        setResetError('Failed to set new password. Please try again.');
      }
    } catch (error) {
      console.error('Reset password failed:', error);
      let message = 'Failed to set new password. Please try again.';
      if (
        axios.isAxiosError(error) &&
        error.response?.data &&
        (error.response.data as any).message
      ) {
        message = (error.response.data as any).message;
      }
      setResetError(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex min-h-svh flex-col bg-[#1a1a1a]">
        <header className="flex items-center border-b border-[#2E3835] px-5 py-3">
          <img src="/logo.svg" alt="Watchful" className="h-7 w-auto" />
        </header>

        <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col items-center justify-center px-8 pt-10 pb-16 text-center">
          <CheckCircle2 className="text-lime mb-7 h-16 w-16" strokeWidth={1.5} />

          <h1 className="mb-3 text-3xl font-bold tracking-tight text-white">
            Password updated
          </h1>
          <p className="mb-12 max-w-xs text-base leading-relaxed text-white/60">
            Your password has been reset. You can now log in to Watchful Go with your new password.
          </p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh flex-col bg-[#1a1a1a]">
      <header className="flex items-center border-b border-[#2E3835] px-5 py-3">
        <img src="/logo.svg" alt="Watchful" className="h-7 w-auto" />
      </header>

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center px-8 pt-10 pb-16">
        <h1 className="mb-2 text-3xl font-bold tracking-tight text-white">
          Reset your <br />
          <span className="text-lime">password</span>
        </h1>
        <p className="mb-10 text-base leading-relaxed text-white/60">
          Enter a new password for your account.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <Label htmlFor="password" className="mb-2 block text-sm text-white/70">
              New Password
            </Label>
            <Input
              id="password"
              type="password"
              className="focus:border-lime w-full border-[#2E3835] bg-[#2a2a2a] p-3 text-base text-white shadow-sm focus:outline-none"
              placeholder="At least 8 characters"
              value={password}
              onChange={handlePasswordChange}
              required
              disabled={isLoading}
              autoFocus
            />
            {passwordError && <p className="mt-2 text-sm text-red-400">{passwordError}</p>}
          </div>

          {resetError && (
            <div className="rounded-md border border-red-500/20 bg-red-500/10 p-3">
              <p className="text-sm text-red-400">{resetError}</p>
            </div>
          )}

          <Button
            type="submit"
            className="bg-lime mt-2 w-full py-6 text-base font-semibold text-black"
            disabled={isLoading || !password}
          >
            {isLoading ? (
              <>
                Setting Password...
                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
              </>
            ) : (
              'Reset Password'
            )}
          </Button>
        </form>
      </main>
    </div>
  );
}
